import axios from 'axios'
import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { store } from '@Redux/store'
import { readCsrfToken, setCsrfToken } from '@Api/csrfToken'

let state: typeof store
export const injectStore = (_state: any) => {
	state = _state
}

/**
 * The session credential is an httpOnly cookie set by the API. It is not
 * readable here and must never be: that is what stops an XSS from stealing it,
 * which localStorage could not do.
 *
 * `withCredentials` / `credentials: 'include'` is therefore load-bearing — the
 * browser will not attach the cookie on a cross-origin request without it, and
 * the portal and API are on different hosts in every environment.
 *
 * The cookie is `SameSite=Lax`, which means the API must also be same-SITE with
 * the portal, not merely reachable: a portal on `stag.abwaabegypt.com` calling
 * an API on a different registrable domain would receive the cookie at login
 * and then never send it again, and every later request would arrive anonymous.
 * CORS does not cover this — it governs whether the browser may USE credentials,
 * SameSite governs whether it sends them at all.
 */
const api = axios.create({
	baseURL: process.env.REACT_APP_API_BASE_URL,
	withCredentials: true,
})

const CSRF_HEADER = 'x-csrf-token'

/**
 * Make sure a CSRF token exists before a mutating request goes out.
 *
 * The token lives in memory, so a page reload starts with none and it is only
 * repopulated when `bootstrapAuthAction` finishes. A student who refreshed and
 * immediately submitted — an exam answer, a profile save — raced that boot and
 * got a 403 on a perfectly good connection.
 *
 * One in-flight fetch, shared by every caller that arrives during the gap. On
 * failure it resolves anyway and the request proceeds without a token: the
 * server will reject it, which is the same outcome as not trying, and blocking
 * forever would be worse.
 */
let csrfFetch: Promise<void> | null = null

/**
 * When the last fetch produced no token.
 *
 * Without this there is no negative caching at all: `setCsrfToken` ignores a
 * falsy value, the `.catch` swallows, and `.finally` clears `csrfFetch` — so a
 * single failing or token-less `/auth/me` left the guard permanently
 * unsatisfied and EVERY subsequent request fired another one, with no backoff
 * and no cap. Request volume roughly doubled and each request gained a
 * serialized round trip, indefinitely.
 */
let csrfFailedAt = 0
const CSRF_RETRY_COOLDOWN_MS = 10_000

/** GET/HEAD/OPTIONS change nothing, so they need no token and never wait for one. */
export const SAFE_METHODS = ['get', 'head', 'options']

export const ensureCsrfToken = async (): Promise<void> => {
	if (readCsrfToken()) return

	// Bail only when we KNOW the caller is anonymous.
	//
	// This used to test `isAuth`, which is false for the whole /auth/me round
	// trip — so it returned instantly during exactly the boot race the comment
	// below describes, and the mutation went out with no token and 403'd. The
	// distinction between "anonymous" and "haven't asked yet" is what
	// `authReady` exists for; only the first should skip the fetch.
	const auth = state?.getState().auth
	if (auth?.authReady && !auth?.isAuth) return
	// Nothing has been asked yet and nothing is in flight — no session to
	// protect, so do not block the request on a fetch that can only 401.
	if (!auth) return

	if (Date.now() - csrfFailedAt < CSRF_RETRY_COOLDOWN_MS) return

	if (!csrfFetch) {
		csrfFetch = api
			.get('/auth/me')
			.then(({ data }) => {
				const token = data?.data?.csrfToken
				setCsrfToken(token)
				csrfFailedAt = token ? 0 : Date.now()
			})
			.catch(() => {
				csrfFailedAt = Date.now()
			})
			.finally(() => {
				csrfFetch = null
			})
	}
	await csrfFetch
}

/**
 * Headers every request needs, in one place.
 *
 * There used to be three separate places attaching credentials — this callback,
 * the axios interceptor below, and a duplicate interceptor registered inside a
 * useEffect in ApiProvider. Three copies of auth logic is three chances to
 * disagree; the token is gone now and what remains lives here.
 */
const commonHeaders = (isEmbed: boolean): Record<string, string> => {
	const headers: Record<string, string> = {
		'accept-language': localStorage.getItem('lang') ?? 'en-GB',
	}
	// No DeviceId header. It carried a client-computed fingerprint that the
	// server used to key device identity; identity is server-minted and rides
	// the device cookie now, so sending it on every request would be a
	// client-supplied identifier that nothing reads — and that a future reader
	// could easily mistake for something meaningful. The fingerprint itself is
	// still sent, once, in the login body, purely as a re-adoption signal.
	if (isEmbed) headers['x-embed'] = 'admin'
	const csrf = readCsrfToken()
	if (csrf) headers[CSRF_HEADER] = csrf
	return headers
}

/**
 * Async on purpose: it awaits the CSRF token before building the headers.
 *
 * This is the single place every RTK Query root goes through, which is why the
 * ensure lives here rather than in one root's baseQuery wrapper. There are two
 * roots — BaseApi and ProgressAPI — and ProgressAPI has its own
 * `fetchBaseQuery`, so a wrapper on BaseApi would cover only half the traffic.
 * Its one endpoint is declared `builder.query` but issues a POST, so it needs
 * the token like any mutation and would 403 on the first request after a
 * page reload.
 *
 * `ensureCsrfToken` is a no-op when a token is already held or the caller is
 * anonymous, so this costs nothing on the common path.
 */
export const rtkPrepareHeadersCallback = async (
	headers: Headers,
	{ getState }: Pick<BaseQueryApi, 'getState' | 'type' | 'extra' | 'endpoint' | 'forced'>
) => {
	await ensureCsrfToken()
	const isEmbed = (getState?.() as any)?.utils?.isEmbed as boolean
	Object.entries(commonHeaders(isEmbed)).forEach(([key, value]) => headers.set(key, value))
	return headers
}

api.interceptors.request.use(
	async function (config) {
		try {
			// Only mutations need the token, and only they are worth delaying for.
			// `/auth/me` is excluded explicitly or the lazy fetch would recurse.
			const method = (config.method ?? 'get').toLowerCase()
			if (!SAFE_METHODS.includes(method) && !config.url?.endsWith('/auth/me')) {
				await ensureCsrfToken()
			}

			const isEmbed = state?.getState().utils.isEmbed ?? false
			// Merge rather than replace: the previous version overwrote
			// config.headers wholesale, silently dropping any per-call header.
			config.headers = { ...(config.headers ?? {}), ...commonHeaders(isEmbed) }
			return config
		} catch (error) {
			return Promise.reject(error)
		}
	},
	function (error) {
		return Promise.reject(error.response)
	}
)

export default api
